/* eslint-disable @atlassian/relay/graphql-naming */
/* eslint-disable jira/filename-case */
import React, { useCallback, memo, type RefCallback, useMemo } from 'react';
import noop from 'lodash/noop';
import isEqual from 'lodash/isEqual';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import { useInlineCreateHandlers } from '@atlassian/jira-issue-table-inline-issue-create/src/ui/index.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { JiraIssueAri } from '@atlassian/ari/jira/issue';
import { getFlatListKey } from '@atlassian/jira-issue-table-hierarchy/src/common/utils/get-flat-list-key/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	useHierarchyActions,
	getShowMoreItemKey,
	getChildIssueCreateItemKey,
	type PopulateConnectionArgs,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import type { GroupContextField } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/types.tsx';
import type { Row_rules$key } from '../row/__generated__/Row_rules.graphql';
import type { Row_project$key } from '../row/__generated__/Row_project.graphql';
import { useShouldApplyFrozenColumns } from '../../controllers/use-should-apply-frozen-columns/index.tsx';
import LoadMoreItemsRow from '../../common/ui/load-more-items-row/index.tsx';
import { COLUMN_ID_CHECKBOX } from '../../common/constants.tsx';
import type { TopLevelIssueCreateConfig } from '../../common/types.tsx';
import {
	useIsIssueHierarchyEnabled,
	useIsVirtualizationEnabled,
	useProjectContext,
	useIsTimelineEnabled,
	useIsStickyColumnsVisible,
} from '../../controllers/features/selectors.tsx';
import Row from '../row/Row.tsx';
import type { Rows_rules$key } from './__generated__/Rows_rules.graphql';
import type { Rows$key, Rows$data } from './__generated__/Rows.graphql';
import type { Rows_project$key } from './__generated__/Rows_project.graphql';
import { RowsAdditionalContentRenderer } from './rows-additional-content-renderer/RowsAdditionalContentRenderer.tsx';
import { useRowContext } from './row-context/index.tsx';
import { VirtualizedRows } from './virtualized-rows-hierarchy/index.tsx';

export type RowsProps = {
	issueResults: Rows$key;
	rankFieldId: string | null | undefined;
	itemsConnectionId: string;
	parentItemId: string;
	depth?: number;
	parentIssueAri?: string;
	parentItemConnectionId?: string;
	parentIssueRelayConnectionId?: string;
	parentIssueHierarchyLevel?: number | null;
	hasNext?: boolean;
	isLoadingNext?: boolean;
	isChildIssueCreateRowVisible?: boolean;
	projectKey?: string | undefined;
	project?: Rows_project$key | null;
	onLoadNextItems?: () => void;
	renderChildIssueCreateRow?: () => React.ReactNode;
	isDraggingRowFaded?: boolean;
	formattingRules?: Rows_rules$key | null;
	groupContextFieldInput?: GroupContextField;
	rowInlineCreateCanShowAdditionalFields: boolean;
	topLevelIssueCreateConfig?: TopLevelIssueCreateConfig;
	initialPopulateConnectionArgs?: PopulateConnectionArgs[];
};

const HierarchyRow = memo(
	({
		project,
		depth,
		itemsConnectionId,
		rankFieldId,
		parentItemConnectionId,
		parentIssueAri,
		parentIssueHierarchyLevel,
		rowsData,
		index,
		isVirtual,
		isIssueHierarchyEnabled,
		measureElement,
		projectKey,
		previousItemId,
		isDraggingRowFaded,
		isStickyColumnsVisible,
		formattingRules,
		rowInlineCreateCanShowAdditionalFields,
		topLevelIssueCreateConfig,
	}: {
		depth: number;
		itemsConnectionId: string;
		rankFieldId: string | null | undefined;
		parentItemConnectionId: string;
		parentIssueAri: string;
		parentIssueHierarchyLevel?: number | null;
		rowsData: Rows$data;
		index: number;
		project?: Row_project$key | null;
		isVirtual?: boolean;
		isIssueHierarchyEnabled?: boolean;
		measureElement?: RefCallback<HTMLTableRowElement>;
		projectKey: string | undefined;
		previousItemId?: string | undefined;
		isDraggingRowFaded?: boolean;
		isStickyColumnsVisible?: boolean;
		formattingRules?: Row_rules$key | null;
		rowInlineCreateCanShowAdditionalFields: boolean;
		topLevelIssueCreateConfig?: TopLevelIssueCreateConfig;
	}) => {
		const issueRow = rowsData?.edges?.[index];
		const hierarchyLevel = issueRow?.node?.issueTypeField?.issueType?.hierarchy?.level;
		const { renderChildRows } = useRowContext();

		const { isFormVisible, formProps, triggerProps } = useInlineCreateHandlers({
			projectKey: useProjectContext(),
			...(hierarchyLevel != null && {
				minHierarchyLevel: hierarchyLevel - 1,
				maxHierarchyLevel: hierarchyLevel - 1,
			}),
		});

		return (
			<ContextualAnalyticsData
				attributes={{
					rowIndex: index,
					tableRowNumber: index,
					...(fg('timeline-issue-table-additional-analytics') && {
						issueId: issueRow?.node?.issueId,
						parentId: parentIssueAri ? JiraIssueAri.parse(parentIssueAri).issueId : undefined,
						issueHierarchy: hierarchyLevel,
					}),
				}}
			>
				{isVirtual && (
					<Row
						project={project}
						rankFieldId={rankFieldId}
						rowIndex={index}
						itemsConnectionId={rowsData.__id}
						issueEdge={issueRow ?? null}
						fieldSets={issueRow?.fieldSets ?? null}
						depth={depth}
						parentIssueAri={parentIssueAri}
						parentItemConnectionId={parentItemConnectionId}
						parentIssueRelayConnectionId={itemsConnectionId}
						parentIssueHierarchyLevel={parentIssueHierarchyLevel}
						triggerProps={triggerProps}
						isFormVisible={isFormVisible}
						measureElement={measureElement}
						previousItemId={previousItemId}
						isDraggingRowFaded={isDraggingRowFaded}
						isStickyColumnsVisible={isStickyColumnsVisible}
						formattingRules={formattingRules}
						topLevelIssueCreateConfig={topLevelIssueCreateConfig}
						rowInlineCreateCanShowAdditionalFields={rowInlineCreateCanShowAdditionalFields}
						{...(fg('jsc_list_reparenting')
							? { hierarchyLevel, issueKey: issueRow?.node?.key, projectKey }
							: {})}
					/>
				)}
				{issueRow?.node?.issueId &&
					isIssueHierarchyEnabled &&
					renderChildRows?.({
						issueKey: issueRow?.node?.key,
						parentItemId: issueRow?.node?.issueId,
						itemsConnectionId: rowsData.__id,
						depth,
						parentIssueAri: issueRow?.node?.id,
						parentItemConnectionId: issueRow?.__id,
						parentIssueRelayConnectionId: itemsConnectionId,
						parentIssueHierarchyLevel: hierarchyLevel,
						type: 'PARENT_CHILDREN',
						isFormVisible,
						formProps,
						hasChildren: issueRow?.hasChildren ?? false,
					})}
			</ContextualAnalyticsData>
		);
	},
);

export const RowsNonMemoized = ({
	project,
	projectKey,
	issueResults,
	rankFieldId,
	depth = 0,
	parentItemId,
	itemsConnectionId,
	parentIssueAri,
	parentItemConnectionId,
	parentIssueRelayConnectionId,
	parentIssueHierarchyLevel,
	isLoadingNext,
	hasNext,
	isChildIssueCreateRowVisible,
	onLoadNextItems,
	renderChildIssueCreateRow,
	isDraggingRowFaded,
	formattingRules,
	groupContextFieldInput,
	rowInlineCreateCanShowAdditionalFields,
	topLevelIssueCreateConfig,
	initialPopulateConnectionArgs,
}: RowsProps) => {
	const rowsData = useFragment<Rows$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment Rows on JiraIssueConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isReparentingEnabled: { type: "Boolean!" }
				isTimelineEnabled: { type: "Boolean!" }
				isDensityFull: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
				isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
				isEditableSyncLoaded: { type: "Boolean!" }
			) {
				__id
				edges {
					__id
					...Row_issueEdge
						@arguments(
							isReparentingEnabled: $isReparentingEnabled
							isDensityFull: $isDensityFull
							projectKeys: $projectKeys
							projectKey: $projectKey
							shouldQueryHasChildren: $shouldQueryHasChildren
							isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
							isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						)
					hasChildren(filterByProjectKeys: $projectKeys)
						@include(if: $shouldQueryHasChildren)
						@optIn(to: "JiraSpreadsheetComponent-M2")
					node {
						id
						key
						issueId
						issueTypeField {
							issueType {
								hierarchy {
									level
								}
							}
						}
					}
					fieldSets(first: 500) @optIn(to: "JiraSpreadsheetComponent-M1") {
						...Row_fieldSets
							@arguments(
								isInlineEditingEnabled: $isInlineEditingEnabled
								isDensityFull: $isDensityFull
								isConditionalFormattingEnabled: $isConditionalFormattingEnabled
								isTimelineEnabled: $isTimelineEnabled
								isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
								isEditableSyncLoaded: $isEditableSyncLoaded
							)
					}
				}
				...RowsAdditionalContentRenderer @arguments(isTimelineEnabled: $isTimelineEnabled)
			}
		`,
		issueResults,
	);

	const projectData = useFragment<Rows_project$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment Rows_project on JiraProject
			@argumentDefinitions(isConditionalFormattingEnabled: { type: "Boolean!" }) {
				...Row_project @arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		project ?? null,
	);

	const formattingRulesData = useFragment<Rows_rules$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment Rows_rules on JiraFormattingRuleConnection {
				...Row_rules
			}
		`,
		formattingRules ?? null,
	);

	const isIssueHierarchyEnabled = useIsIssueHierarchyEnabled();
	const { populateConnection } = useHierarchyActions();

	const { columns, groupId } = useRowContext();
	let isStickyColumnsVisible = false;
	if (fg('jira_list_frozen_column_bugfix')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isStickyColumnsVisible = useIsStickyColumnsVisible();
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { isStickyColumnsVisible: isStickyColumnsVisibleOld } = useShouldApplyFrozenColumns();
		isStickyColumnsVisible = isStickyColumnsVisibleOld;
	}
	const isTimelineEnabled = useIsTimelineEnabled();
	const hasCheckboxColumn = columns.length > 0 && columns[0].id === COLUMN_ID_CHECKBOX;

	const populateConnectionArgs = useMemo<PopulateConnectionArgs>(
		() => ({
			parentItemId,
			itemsConnectionId: rowsData.__id,
			parentConnectionId: itemsConnectionId,
			list:
				rowsData?.edges
					?.map(
						(edge) =>
							edge?.node && {
								key: edge.node.key,
								itemId: edge.node.issueId,
								groupId,
							},
					)
					.filter(Boolean) ?? [],
			hasNext,
			groupId,
			isChildIssueCreateRowVisible,
		}),
		[
			parentItemId,
			itemsConnectionId,
			rowsData.__id,
			rowsData?.edges,
			hasNext,
			groupId,
			isChildIssueCreateRowVisible,
		],
	);
	/*
	 *  initialPopulateConnectionArgs is used to initialise the hierarchy store container for root connections, and will
	 *  hence prevent calling populateConnection for root connections if the root connection matches populateConnectionArgs.
	 *
	 *  On initial load, Rows.tsx can be rendered as root connection for hierarchy on/off.
	 *  However, for grouping, Rows.tsx is used to render the first group connection, which is difficult to initialise
	 *  as there's a store-only lazy loaded query FirstGroupIssuesQuery we'd need to mimic to get the right itemsConnectionId.
	 *  So for now, we'll allow populateConnection to proceed for the first group rows, which will cause later render.
	 *
	 *  If the connection arguments mismatch for any reason, we gracefully populateConnection, but will pay a render interruption penalty.
	 */

	const prevPopulateConnectArgs: PopulateConnectionArgs | undefined =
		usePrevious(populateConnectionArgs) ??
		// eslint-disable-next-line jira/ff/no-preconditioning
		(fg('initialise_virtualiser_state_from_relay_store') && initialPopulateConnectionArgs
			? initialPopulateConnectionArgs.find((args) => args.parentItemId === 'root')
			: undefined);

	if (!isEqual(populateConnectionArgs, prevPopulateConnectArgs)) {
		populateConnection(populateConnectionArgs);
	}

	const renderRowItem = useCallback(
		(index: number, measureElement?: RefCallback<HTMLTableRowElement>) => {
			const issueRow = rowsData?.edges?.[index];

			return (
				<ContextualAnalyticsData
					key={issueRow?.node?.key || index}
					attributes={{
						rowIndex: index,
						tableRowNumber: index,
						...(fg('timeline-issue-table-additional-analytics') && {
							issueId: issueRow?.node?.issueId,
							parentId: parentIssueAri ? JiraIssueAri.parse(parentIssueAri).issueId : undefined,
							issueHierarchy: issueRow?.node?.issueTypeField?.issueType?.hierarchy?.level,
						}),
					}}
				>
					<Row
						project={projectData}
						rowIndex={index}
						rankFieldId={rankFieldId}
						itemsConnectionId={rowsData.__id}
						issueEdge={issueRow ?? null}
						fieldSets={issueRow?.fieldSets ?? null}
						depth={depth}
						parentIssueAri={parentIssueAri}
						parentItemConnectionId={parentItemConnectionId}
						parentIssueRelayConnectionId={parentIssueRelayConnectionId}
						parentIssueHierarchyLevel={parentIssueHierarchyLevel}
						measureElement={measureElement}
						isDraggingRowFaded={isDraggingRowFaded}
						isStickyColumnsVisible={isStickyColumnsVisible}
						formattingRules={formattingRulesData}
						{...(fg('jsc_list_reparenting') ? { projectKey, issueKey: issueRow?.node?.key } : {})}
						{...(fg('group_by_any_field_sibling_inline_issue_create') && {
							groupContextFieldInput,
						})}
						rowInlineCreateCanShowAdditionalFields={rowInlineCreateCanShowAdditionalFields}
					/>
				</ContextualAnalyticsData>
			);
		},
		[
			rowsData?.edges,
			rowsData.__id,
			projectData,
			rankFieldId,
			depth,
			parentIssueAri,
			parentItemConnectionId,
			parentIssueRelayConnectionId,
			parentIssueHierarchyLevel,
			isDraggingRowFaded,
			isStickyColumnsVisible,
			formattingRulesData,
			projectKey,
			groupContextFieldInput,
			rowInlineCreateCanShowAdditionalFields,
		],
	);

	const renderRows = useCallback(
		(
			isItemVirtual?: (itemId: string) => boolean,
			measureElement?: RefCallback<HTMLTableRowElement>,
		) => {
			const childIssueCreateItemId = getFlatListKey({
				itemId: getChildIssueCreateItemKey(parentItemId),
				groupId,
			});
			const isChildIssueCreateRowVirtual = isItemVirtual?.(childIssueCreateItemId) ?? true;
			const showMoreItemId = getFlatListKey({
				itemId: getShowMoreItemKey(parentItemId),
				groupId,
			});
			const isShowMoreItemVirtual = isItemVirtual?.(showMoreItemId) ?? true;

			return (
				<>
					{isChildIssueCreateRowVirtual && renderChildIssueCreateRow?.()}
					{rowsData?.edges?.map((edge, index: number) => {
						const flatListIssueId = getFlatListKey({
							itemId: edge?.node?.issueId ?? '',
							groupId,
						});

						let isVirtual = isItemVirtual?.(flatListIssueId) ?? true;

						// we're using row index as virtualisation key fallback when flatList is empty
						// hence the check includes both itemId and index to avoid rows flickering on transition
						if (fg('jira_list_isvirtual_by_index')) {
							isVirtual = (isItemVirtual?.(flatListIssueId) || isItemVirtual?.(`${index}`)) ?? true;
						}

						const previousItemId =
							index > 0 && fg('jira_timeline_cell_border_style')
								? rowsData?.edges?.[index - 1]?.node?.issueId
								: undefined;

						return isIssueHierarchyEnabled ? (
							<HierarchyRow
								project={projectData}
								key={flatListIssueId}
								rankFieldId={rankFieldId}
								itemsConnectionId={itemsConnectionId}
								isVirtual={isVirtual}
								index={index}
								rowsData={rowsData}
								depth={depth}
								parentItemConnectionId={parentItemConnectionId ?? ''}
								parentIssueAri={parentIssueAri ?? ''}
								parentIssueHierarchyLevel={parentIssueHierarchyLevel}
								isIssueHierarchyEnabled={isIssueHierarchyEnabled}
								measureElement={measureElement}
								projectKey={projectKey}
								previousItemId={previousItemId}
								isDraggingRowFaded={isDraggingRowFaded}
								isStickyColumnsVisible={isStickyColumnsVisible}
								formattingRules={formattingRulesData}
								rowInlineCreateCanShowAdditionalFields={rowInlineCreateCanShowAdditionalFields}
								topLevelIssueCreateConfig={topLevelIssueCreateConfig}
							/>
						) : (
							isVirtual && renderRowItem(index, measureElement)
						);
					})}
					{hasNext && isShowMoreItemVirtual && (
						<LoadMoreItemsRow
							isLoadingNext={isLoadingNext}
							columnsCount={columns.length}
							loadNext={onLoadNextItems ?? noop}
							isStickyColumnsVisible={isStickyColumnsVisible}
							{...(isTimelineEnabled || fg('jira_timeline_harmonisation_m1_issue_table_updates')
								? { hasCheckboxColumn }
								: {})}
						/>
					)}
					<RowsAdditionalContentRenderer depth={depth} groupId={groupId} issues={rowsData} />
				</>
			);
		},
		[
			parentItemId,
			groupId,
			renderChildIssueCreateRow,
			rowsData,
			hasNext,
			isLoadingNext,
			columns.length,
			onLoadNextItems,
			isStickyColumnsVisible,
			isTimelineEnabled,
			hasCheckboxColumn,
			depth,
			isIssueHierarchyEnabled,
			projectData,
			rankFieldId,
			itemsConnectionId,
			parentItemConnectionId,
			parentIssueAri,
			parentIssueHierarchyLevel,
			projectKey,
			isDraggingRowFaded,
			formattingRulesData,
			rowInlineCreateCanShowAdditionalFields,
			topLevelIssueCreateConfig,
			renderRowItem,
		],
	);

	const isVirtualizationEnabled = useIsVirtualizationEnabled();
	if (isVirtualizationEnabled) {
		const isGrouping = Boolean(groupId);
		return (
			<VirtualizedRows hasSpacers={depth === 0 && !isGrouping} columnsCount={columns.length}>
				{renderRows}
			</VirtualizedRows>
		);
	}

	return renderRows();
};

const Rows = componentWithFG(
	'memoise_issuetable_components',
	memo(RowsNonMemoized),
	RowsNonMemoized,
);

export default Rows;
